import ENV from 'config/environment'
import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams, useRouteMatch } from 'react-router'
import get from 'lodash/get'
import styled from 'styled-components'
import { H, Divider, Wrapper, Button, Grid } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import Table from 'components/table'
import useInvoices from 'hooks/useInvoices'
import { formatCurrency } from 'lib/formatting/currency'
import { launchPciPalSession } from 'lib/pci-pal/launch-session'
import { FuneralPlan, FuneralPlanPaymentDetails } from 'lib/models/funeral-plan'
import { Invoice } from 'lib/models/invoice'
import { FuneralPlanPaymentType, Lead } from 'lib/models/lead'
import {
  fetchFuneralPlanPaymentDetails,
  generatePciPalSession,
  hideModal,
  showModal,
} from 'state/actions'
import { stripeUrlForCustomerPage } from 'utils/helpers'
import BalancePaymentModal, {
  type ThirdPartyPayer,
} from './balance-payment-modal'
import { CancelFuneralPlan } from './cancel-funeral-plan'
import PaymentQuoteHistory from './payment-quote-history'
import PaymentTimeline from './payment-timeline'
import PaymentCurrentPlan from './payment-current-plan'
import { currentPlanOf, timelineOf } from './payment-versions'
import SetupOnStripeModal from './setup-on-stripe-modal'

const StyledRowWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  margin: ${GTR.M} 0;
  justify-content: space-between;
  align-items: baseline;
`

const StyledButtonWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  gap: ${GTR.M};
  align-items: baseline;
`

const StyledButton = styled(Button.Primary)`
  margin-top: 10px;
  min-width: 215px;
`

/** The current plan card's buttons are the small size, as in the design, so
 * they fit beside the plan's name on a card of half the width */
const CardButton = (
  props: React.ComponentProps<typeof Button.Primary>
): React.ReactElement => <Button.Primary size="S" {...props} />

const Payment = (): React.ReactElement => {
  const { id: funeralPlanId } = useParams<{ id: string }>()
  const showCancelPlan = useRouteMatch({
    path: '/funeral-plans/:id/payment/cancel-plan',
    exact: true,
  })?.isExact

  const dispatch = useDispatch()
  const { funeralPlan, funeralPlanPaymentDetails } = useSelector(
    (state: {
      funeralPlanCase: FuneralPlan
      funeralPlanPaymentDetails: FuneralPlanPaymentDetails
    }) => ({
      funeralPlan: state.funeralPlanCase,
      funeralPlanPaymentDetails: state.funeralPlanPaymentDetails,
    }),
    shallowEqual
  )

  useEffect(() => {
    dispatch(fetchFuneralPlanPaymentDetails(funeralPlanId))
  }, [funeralPlanId, dispatch])

  const { invoices, fetchInvoices } = useInvoices()

  useEffect(() => {
    if (funeralPlanId) {
      fetchInvoices({ queryParams: { 'filter[funeralPlanId]': funeralPlanId } })
    }
  }, [fetchInvoices, funeralPlanId])

  useEffect(() => {
    if (!showCancelPlan || !funeralPlanPaymentDetails.type) return

    const inputEl = document.getElementById('cancel-plan')
    if (!inputEl) return

    const topOfSection = inputEl.getBoundingClientRect().top + window.scrollY
    const pointToScrollTo = topOfSection - parseInt(GTR.XL)
    window.scrollTo(0, pointToScrollTo)
  }, [showCancelPlan, funeralPlanPaymentDetails])

  if (!funeralPlanPaymentDetails.type) {
    return <></>
  }

  const handleGoToCustomerStripe = async () => {
    if (!funeralPlanPaymentDetails.attributes.stripeCustomerId) return

    window.open(
      stripeUrlForCustomerPage(
        funeralPlanPaymentDetails.attributes.stripeCustomerId
      )
    )
  }

  const openPciPalTab = async ({
    referenceId,
    paymentType,
    amountInPence,
    thirdPartyPayer,
  }: {
    referenceId: Lead['attributes']['uuid']
    paymentType: FuneralPlanPaymentType
    amountInPence: number
    /** A third party paying on behalf of the purchaser. Omitted when the purchaser pays. */
    thirdPartyPayer?: ThirdPartyPayer
  }): Promise<void> => {
    try {
      const response = await dispatch(
        generatePciPalSession({
          referenceId,
          paymentType,
          amountInPence,
          product: 'funeral_plan',
          thirdPartyPayer,
        })
      )

      const data = get(response, 'value.data.data')
      const url = get(data, 'url')
      const accessToken = get(data, 'accessToken')
      const refreshToken = get(data, 'refreshToken')
      launchPciPalSession({ url, accessToken, refreshToken })
    } catch {
      dispatch(hideModal())
    }
  }

  const handlePayBalanceByPciPalModal = ({
    referenceId,
    amountInPence,
  }: {
    referenceId: Lead['attributes']['uuid']
    amountInPence: number
  }): void => {
    dispatch(
      showModal({
        component: BalancePaymentModal,
        headingText: 'Take balance payment with PCI PAL',
        config: {
          amountInPence,
          openPciPalTab: (thirdPartyPayer?: ThirdPartyPayer) =>
            openPciPalTab({
              referenceId,
              paymentType: 'in_full',
              amountInPence,
              thirdPartyPayer,
            }),
        },
      })
    )
  }

  const handleSetupOnStripeModal = ({
    referenceId,
    monthlyAmountInPence,
  }: {
    referenceId: Lead['attributes']['uuid']
    monthlyAmountInPence: number
  }): void => {
    dispatch(
      showModal({
        component: SetupOnStripeModal,
        headingText: 'Set customer up on Stripe',
        config: {
          monthlyAmountInPence,
          openPciPalTab: () =>
            openPciPalTab({
              referenceId,
              paymentType: 'instalments',
              amountInPence: monthlyAmountInPence,
            }),
        },
      })
    )
  }

  const paidInvoices =
    invoices?.filter((invoice: Invoice) => invoice?.attributes.paidOn) ?? []
  const hasOnlySquarePayments =
    paidInvoices.length > 0 &&
    paidInvoices.every(
      (invoice: Invoice) => invoice.attributes.paymentProvider === 'square'
    )

  const shouldShowSetupOnStripeButton =
    ENV.FF_STRIPE_SWITCH_BUTTON_ENABLED &&
    funeralPlan.attributes.status === 'active' &&
    funeralPlanPaymentDetails.attributes.type === 'Instalments' &&
    !['Paid', 'Refunded'].includes(
      funeralPlanPaymentDetails.attributes.status
    ) &&
    hasOnlySquarePayments

  /** Every agreement but drafts, which the server leaves out */
  const versions = funeralPlanPaymentDetails.attributes.versions
  /** None until an agreement is part of the plan, when the page keeps its
   * old layout */
  const currentPlan = currentPlanOf({
    versions,
    planName: funeralPlanPaymentDetails.attributes.planName ?? '',
    separateCelebration: Boolean(
      funeralPlanPaymentDetails.attributes.separateCelebration
    ),
    celebrationName: funeralPlanPaymentDetails.attributes.celebrationName,
  })
  const timelineRows = timelineOf({
    versions,
    unlinkedMoney: funeralPlanPaymentDetails.attributes.unlinkedMoney,
  })

  const customerActions = ['open', 'active'].includes(
    funeralPlan.attributes.status
  ) && (
    <>
      {funeralPlanPaymentDetails.attributes.stripeCustomerId && (
        <Button.Primary onClick={() => handleGoToCustomerStripe()}>
          Customer on Stripe
        </Button.Primary>
      )}
    </>
  )

  /* If there is an outstanding balance, show the "Pay Balance" button,
   * even if the customer did not pay by Stripe before. */
  const ActionButton = currentPlan ? CardButton : StyledButton
  const balanceActions = !['Paid', 'Refunded'].includes(
    funeralPlanPaymentDetails.attributes.status
  ) ? (
    <>
      {
        /* Prevents Farewill plans with negative outstanding balance (due to auto-calculation of £100 instalment fee refund) from being shown "Pay Balance" button */
        funeralPlanPaymentDetails.attributes.amountToClearBalanceInPence &&
          funeralPlanPaymentDetails.attributes.amountToClearBalanceInPence >
            0 && (
            <ActionButton
              onClick={() =>
                handlePayBalanceByPciPalModal({
                  referenceId: funeralPlan.attributes.leadUuid as string,
                  amountInPence:
                    funeralPlanPaymentDetails.attributes
                      .amountToClearBalanceInPence || 0,
                })
              }
            >
              Pay Balance
            </ActionButton>
          )
      }
      {shouldShowSetupOnStripeButton && (
        <ActionButton
          onClick={() =>
            handleSetupOnStripeModal({
              referenceId: funeralPlan.attributes.leadUuid as string,
              monthlyAmountInPence:
                funeralPlanPaymentDetails.attributes.monthlyPriceInPence || 0,
            })
          }
        >
          Set customer up on Stripe
        </ActionButton>
      )}
    </>
  ) : null

  return (
    <Wrapper>
      <H tag="h2" size="M">
        Payments
      </H>
      {currentPlan && (
        <Wrapper margin={[0, 0, 'XL']}>
          <PaymentCurrentPlan
            currentPlan={currentPlan}
            figures={funeralPlanPaymentDetails.attributes}
            cancellationPending={Boolean(
              funeralPlanPaymentDetails.attributes.cancellationPending
            )}
            cancellationFailed={Boolean(
              funeralPlanPaymentDetails.attributes.cancellationFailed
            )}
            celebrationCancellation={
              funeralPlanPaymentDetails.attributes.celebrationCancellation
            }
            cancelled={funeralPlan.attributes.status === 'cancelled'}
            amountToRefundInPence={
              funeralPlanPaymentDetails.attributes.amountToRefundInPence
            }
            actions={balanceActions}
          />
        </Wrapper>
      )}
      {!currentPlan && (
        <Grid>
          <Grid.Item span={6}>
            <H tag="h3" size="S">
              Sale
            </H>
            <Table>
              <Table.Row gridTemplateCols="1fr 1fr">
                <Table.Col>Left to pay</Table.Col>
                <Table.Col strong>
                  {formatCurrency({
                    showPence: true,
                    value:
                      funeralPlanPaymentDetails.attributes.leftToPayInPence,
                    valueInPence: true,
                  })}
                </Table.Col>
              </Table.Row>
              <Table.Row gridTemplateCols="1fr 1fr">
                <Table.Col>Unpaid invoices</Table.Col>
                <Table.Col>
                  {formatCurrency({
                    showPence: true,
                    value:
                      funeralPlanPaymentDetails.attributes
                        .unpaidInvoicesInPence,
                    valueInPence: true,
                  })}
                </Table.Col>
              </Table.Row>
              <Table.Row gridTemplateCols="1fr 1fr">
                <Table.Col>Total quoted</Table.Col>
                <Table.Col>
                  {formatCurrency({
                    showPence: true,
                    value:
                      funeralPlanPaymentDetails.attributes.totalPriceInPence,
                    valueInPence: true,
                  })}
                </Table.Col>
              </Table.Row>
              <Table.Row gridTemplateCols="1fr 1fr">
                <Table.Col>Total paid</Table.Col>
                <Table.Col strong>
                  {formatCurrency({
                    showPence: true,
                    value:
                      funeralPlanPaymentDetails.attributes.totalPaidInPence,
                    valueInPence: true,
                  })}
                </Table.Col>
              </Table.Row>
            </Table>
          </Grid.Item>
          <Grid.Item span={6}>
            <H tag="h3" size="S">
              Status
            </H>
            <Table>
              <Table.Row gridTemplateCols="1fr 1fr">
                <Table.Col>Status</Table.Col>
                <Table.Col strong>
                  {funeralPlanPaymentDetails.attributes.status}
                </Table.Col>
              </Table.Row>
              <Table.Row gridTemplateCols="1fr 1fr">
                <Table.Col>Type</Table.Col>
                <Table.Col>
                  {funeralPlanPaymentDetails.attributes.type}
                </Table.Col>
              </Table.Row>
              {funeralPlanPaymentDetails.attributes.type === 'Instalments' && (
                <Table.Row gridTemplateCols="1fr 1fr">
                  <Table.Col>Instalments paid</Table.Col>
                  <Table.Col>
                    {
                      funeralPlanPaymentDetails.attributes
                        .numberOfInstalmentsPaid
                    }{' '}
                    of{' '}
                    {
                      funeralPlanPaymentDetails.attributes
                        .numberOfInstalmentsTotal
                    }
                  </Table.Col>
                </Table.Row>
              )}
            </Table>
          </Grid.Item>
          {balanceActions && (
            <StyledRowWrapper margin={['L', 0, 0]}>
              <StyledButtonWrapper>{balanceActions}</StyledButtonWrapper>
            </StyledRowWrapper>
          )}
        </Grid>
      )}

      <Wrapper margin={['XL', 0, 0]}>
        <PaymentTimeline rows={timelineRows} actions={customerActions} />
      </Wrapper>

      {versions.length > 0 ? (
        <Wrapper margin={['XL', 0, 0]}>
          <PaymentQuoteHistory versions={versions} />
        </Wrapper>
      ) : (
        <>
          <Divider margin={['XL', 0]} />
          <Wrapper>
            <Table withHeaders>
              <H size="S">Quote</H>
              <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                <Table.Col strong>Description</Table.Col>
                <Table.Col strong>Units</Table.Col>
                <Table.Col strong>Amount</Table.Col>
              </Table.Row>
              <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                <Table.Col>Funeral plan price</Table.Col>
                <Table.Col>1</Table.Col>
                <Table.Col>
                  {formatCurrency({
                    showPence: true,
                    value: funeralPlanPaymentDetails.attributes.packagePrice,
                    valueInPence: true,
                  })}
                </Table.Col>
              </Table.Row>
              {funeralPlanPaymentDetails.attributes.celebrationPriceInPence && (
                <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                  <Table.Col>Celebration</Table.Col>
                  <Table.Col>1</Table.Col>
                  <Table.Col>
                    {formatCurrency({
                      showPence: true,
                      value:
                        funeralPlanPaymentDetails.attributes
                          .celebrationPriceInPence,
                      valueInPence: true,
                    })}
                  </Table.Col>
                </Table.Row>
              )}
              {funeralPlanPaymentDetails.attributes.discountAmountInPence && (
                <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                  <Table.Col>Discount</Table.Col>
                  <Table.Col>1</Table.Col>
                  <Table.Col>
                    -
                    {formatCurrency({
                      showPence: true,
                      value:
                        funeralPlanPaymentDetails.attributes
                          .discountAmountInPence,
                      valueInPence: true,
                    })}
                  </Table.Col>
                </Table.Row>
              )}
              {funeralPlanPaymentDetails.attributes.type === 'Instalments' &&
                funeralPlanPaymentDetails.attributes
                  .numberOfInstalmentsTotal && (
                  <>
                    <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                      <Table.Col>Instalments fee</Table.Col>
                      <Table.Col>1</Table.Col>
                      <Table.Col>
                        {formatCurrency({
                          showPence: true,
                          value:
                            funeralPlanPaymentDetails.attributes.instalmentsFee,
                          valueInPence: true,
                        })}
                      </Table.Col>
                    </Table.Row>
                    <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                      <Table.Col>Single Instalment</Table.Col>
                      <Table.Col>
                        {
                          funeralPlanPaymentDetails.attributes
                            .numberOfInstalmentsTotal
                        }
                      </Table.Col>
                      <Table.Col>
                        {formatCurrency({
                          showPence: true,
                          value:
                            funeralPlanPaymentDetails.attributes
                              .monthlyPriceInPence,
                          valueInPence: true,
                        })}
                      </Table.Col>
                    </Table.Row>
                  </>
                )}
            </Table>
          </Wrapper>
        </>
      )}
      {showCancelPlan && (
        <Wrapper id="cancel-plan" padding={[GTR.XXL, 0]}>
          <CancelFuneralPlan
            invoices={invoices}
            funeralPlanPaymentDetails={funeralPlanPaymentDetails}
            refreshDetails={() => {
              dispatch(fetchFuneralPlanPaymentDetails(funeralPlanId))
            }}
          />
        </Wrapper>
      )}
    </Wrapper>
  )
}

export default Payment
